/**
 * Creator Handoff Module
 * 
 * Handles the "Save your groups first?" flow when anonymous creators
 * try to download the mobile app. Sends a magic link via email to
 * restore their groups in the app.
 */

import { renderModal, closeModal } from './ui.mjs';
import { STORE_LINKS } from './config.mjs';
import t from 'virtual:i18n';

const LS_GROUPS_KEY = 'splitpal:groups';

/**
 * Checks if the user is a "Creator" (has locally created groups)
 * @returns {boolean} True if user has groups in localStorage
 */
export function isCreator() {
    try {
        const raw = localStorage.getItem(LS_GROUPS_KEY);
        if (!raw) return false;
        const groups = JSON.parse(raw);
        return Array.isArray(groups) && groups.length > 0;
    } catch (e) {
        console.warn('Failed to check creator status', e);
        return false;
    }
}

/**
 * Gets all group IDs from localStorage
 * @returns {string[]} Array of group IDs
 */
function getLocalGroupIds() {
    try {
        const raw = localStorage.getItem(LS_GROUPS_KEY);
        if (!raw) return [];
        const groups = JSON.parse(raw);
        if (!Array.isArray(groups)) return [];
        return groups.map(g => g.id).filter(Boolean);
    } catch (e) {
        console.warn('Failed to get local group IDs', e);
        return [];
    }
}

/**
 * Requests a sync token by calling sendGroupMagicLink with a dummy email
 * @returns {Promise<string|null>} The token, or null if failed
 */
async function requestSyncToken() {
    const groupIds = getLocalGroupIds();

    if (groupIds.length === 0) {
        console.warn('No groups to send');
        return null;
    }

    try {
        const { sendGroupMagicLink } = await import('./api.mjs');
        // We use a dummy email since the backend no longer sends an email,
        // it just generates and returns the token.
        return await sendGroupMagicLink('none@none.com', groupIds);
    } catch (e) {
        console.error('Failed to get sync token', e);
        return null;
    }
}

/**
 * Redirects to the appropriate app store
 */
function redirectToStore() {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isAndroid = /Android/.test(navigator.userAgent);

    const storeLinks = STORE_LINKS;

    if (isIOS) {
        window.open(storeLinks.ios, '_blank');
    } else if (isAndroid) {
        window.open(storeLinks.android, '_blank');
    } else {
        // Desktop or unknown - default to iOS
        window.open(storeLinks.ios, '_blank');
    }
}

/**
 * Starts the creator handoff flow by setting the persistence flag,
 * opening the store, and rendering the full-screen UI.
 */
export function startCreatorHandoff() {
    localStorage.setItem('splitpal_pending_sync', 'true');
    redirectToStore();
    renderSyncScreen();
}

/**
 * Helper to get the correctly pluralized word for "tab"
 * @param {number} count 
 * @returns {string}
 */
function getTabWord(count) {
    if (count % 10 === 1 && count % 100 !== 11) return t.tab1;
    if (count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 10 || count % 100 >= 20)) return t.tab2;
    return t.tab5;
}

/**
 * Renders the full-screen "Sync Groups" view
 * @returns {Promise<void>}
 */
export async function renderSyncScreen() {
    const groupIds = getLocalGroupIds();
    
    // If no groups, clear flag and go back to normal flow
    if (groupIds.length === 0) {
        localStorage.removeItem('splitpal_pending_sync');
        window.location.reload();
        return;
    }

    // Hide global header and footer for full screen immersion
    const header = document.getElementById('app-header');
    if (header) header.style.display = 'none';
    const footer = document.getElementById('global-footer');
    if (footer) footer.style.display = 'none';

    const appContainer = document.getElementById('app-container');
    if (!appContainer) return;

    // Clear existing app content
    appContainer.innerHTML = '';

    // Create the full-screen container
    const container = document.createElement('div');
    container.style.cssText = `
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 100dvh;
        padding: 24px;
        box-sizing: border-box;
        text-align: center;
        max-width: 400px;
        margin: 0 auto;
    `;

    // Headline
    const headline = document.createElement('h1');
    headline.textContent = (groupIds.length === 1 && t.syncYourTabs1) ? t.syncYourTabs1 : t.syncYourTabs;
    headline.style.cssText = `
        font-size: 32px;
        font-weight: 700;
        color: #1D1B20;
        margin: 0 0 16px 0;
        letter-spacing: -0.5px;
    `;

    // Body text
    const tabWord = getTabWord(groupIds.length);
    const bodyText = document.createElement('p');
    const descriptionTemplate = (groupIds.length === 1 && t.syncDescription1) ? t.syncDescription1 : t.syncDescription;
    bodyText.innerHTML = descriptionTemplate
        .replace('{count}', groupIds.length)
        .replace('{tabs}', tabWord);
    bodyText.style.cssText = `
        font-size: 18px;
        line-height: 1.5;
        color: #49454F;
        margin: 0 0 40px 0;
    `;

    // Primary button
    const primaryButton = document.createElement('button');
    const buttonTemplate = (groupIds.length === 1 && t.syncButtonText1) ? t.syncButtonText1 : t.syncButtonText;
    primaryButton.innerHTML = buttonTemplate
        .replace('{count}', groupIds.length)
        .replace('{tabs}', tabWord.charAt(0).toUpperCase() + tabWord.slice(1));
    primaryButton.style.cssText = `
        width: 100%;
        padding: 16px 24px;
        background-color: var(--color-primary);
        color: white;
        border: none;
        border-radius: 50px;
        font-size: 18px;
        font-weight: 600;
        cursor: pointer;
        transition: background-color 0.2s ease, transform 0.1s ease;
        margin-bottom: 16px;
        box-shadow: 0 4px 12px rgba(var(--color-primary-rgb), 0.2);
    `;

    primaryButton.addEventListener('mouseenter', () => {
        primaryButton.style.backgroundColor = '#5640A0';
    });

    primaryButton.addEventListener('mouseleave', () => {
        primaryButton.style.backgroundColor = 'var(--color-primary)';
    });

    primaryButton.addEventListener('mousedown', () => {
        primaryButton.style.transform = 'scale(0.98)';
    });

    primaryButton.addEventListener('mouseup', () => {
        primaryButton.style.transform = 'scale(1)';
    });

    // Secondary link
    const secondaryLink = document.createElement('a');
    secondaryLink.textContent = t.noThanks;
    secondaryLink.href = '#';
    secondaryLink.style.cssText = `
        font-size: 16px;
        color: var(--color-primary);
        text-align: center;
        text-decoration: none;
        cursor: pointer;
        padding: 12px;
        font-weight: 500;
    `;

    secondaryLink.addEventListener('mouseenter', () => {
        secondaryLink.style.textDecoration = 'underline';
    });

    secondaryLink.addEventListener('mouseleave', () => {
        secondaryLink.style.textDecoration = 'none';
    });

    // Assemble the body
    container.appendChild(headline);
    container.appendChild(bodyText);
    container.appendChild(primaryButton);
    container.appendChild(secondaryLink);

    appContainer.appendChild(container);

    // Primary button click handler
    primaryButton.addEventListener('click', async (e) => {
        e.preventDefault();

        const { trackEvent } = await import('./utils.mjs');
        trackEvent('sync_tabs_with_app');

        // Show loading state
        const originalText = primaryButton.innerHTML;
        primaryButton.disabled = true;
        primaryButton.style.cursor = 'not-allowed';
        primaryButton.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="animation: spin 1s linear infinite;">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M12 6v6l4 2"></path>
            </svg>
            <span style="margin-left: 12px;">${t.syncing}</span>
        `;

        // Add spinner animation if not present
        if (!document.getElementById('spin-animation')) {
            const style = document.createElement('style');
            style.id = 'spin-animation';
            style.textContent = `
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `;
            document.head.appendChild(style);
        }

        // Fetch token
        const token = await requestSyncToken();
        if (token) {
            window.location.href = `https://tabs.splitpal.io/claim/${token}`;
            
            // Give the browser time to process the deep link before resetting and reloading
            setTimeout(() => {
                localStorage.removeItem('splitpal_pending_sync');
                window.location.reload();
            }, 1000);
        } else {
            alert(t.failedToGenerateSyncLink);
            primaryButton.innerHTML = originalText;
            primaryButton.disabled = false;
            primaryButton.style.cursor = 'pointer';
        }
    });

    // Secondary link click handler
    secondaryLink.addEventListener('click', async (e) => {
        e.preventDefault();

        const { trackEvent } = await import('./utils.mjs');
        trackEvent('no_thanks_sync');

        // Remove the flag and reload the app to resume normal flow
        localStorage.removeItem('splitpal_pending_sync');
        window.location.reload();
    });
}
